import React from "react";
import { useTranslation } from "react-i18next";
import {
  useGetCompareListQuery,
  useGetCompareItemsDataQuery,
} from "../../api/compareApi";
import EmptyState from "../../../cart/components/common/EmptyState";
import PageFeaturedProducts from "../../../featured/components/Page/PageFeaturedProducts";
import { sortProductsByIds } from "../../../wishlist/helpers/sortProductsByIds";
import CompareTable from "./CompareTable/CompareTable";
import Skeleton from "react-loading-skeleton";

const ComparePage: React.FC = () => {
  const { t, ready: areTranslationsReady } = useTranslation();

  // 1. Get IDs from LocalStorage
  const { data: itemIds, isLoading: isIdsLoading } = useGetCompareListQuery();

  // 2. Fetch Products based on IDs using the CUSTOM BATCH ENDPOINT
  // This allows us to render specific Variations with full enriched data.
  const shouldFetchProducts = !!itemIds && itemIds.length > 0;
  const {
    data: products,
    isLoading: areProductsLoading,
    isError,
  } = useGetCompareItemsDataQuery(itemIds || [], {
    skip: !shouldFetchProducts,
  });

  const isComponentLoading =
    isIdsLoading ||
    (shouldFetchProducts && areProductsLoading) ||
    !areTranslationsReady;

  // --- Render Logic ---

  if (isComponentLoading) {
    return (
      <div className="compare-page">
        <div className="compare-page__container">
          <h1>
            <Skeleton width={200} />
          </h1>
          <Skeleton height={400} />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="compare-page">
        <div className="compare-page__container">
          <p>{t("comparePage.error", "Error loading compare list.")}</p>
        </div>
      </div>
    );
  }

  if (!itemIds || itemIds.length === 0 || !products || products.length === 0) {
    return (
      <div className="compare-page compare-page--empty">
        <div className="compare-page__container">
          <EmptyState
            iconName="arrows-right-left"
            title={t("comparePage.emptyTitle", "Compare List Empty")}
            message={t(
              "comparePage.emptyMessage",
              "Add products to compare them side-by-side."
            )}
            buttonText={t("comparePage.returnToShop", "Return to Shop")}
            buttonUrl={window.commonVariables?.shopUrl || "/"}
          >
            <PageFeaturedProducts />
          </EmptyState>
        </div>
      </div>
    );
  }

  // Sort products to maintain user's added order
  const sortedProducts = sortProductsByIds(products, itemIds);

  return (
    <div className="compare-page">
      <div className="compare-page__container">
        <h1 className="compare-page__title">
          {t("comparePage.title", "Compare Products")}
        </h1>

        <div className="compare-page__content">
          <CompareTable products={sortedProducts} />
        </div>
      </div>
    </div>
  );
};

export default ComparePage;
